import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box, Grid, Button, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import { days } from 'constants';
import useStyles from './Rules.styled';
import { BaseModal } from 'components/UI';
import { parseRulesHoursByDays } from 'services/rules';
import { Formik, Form, Field, FieldArray } from 'formik';
import { AddCircle, FileCopy, Clear } from '@material-ui/icons';
import { DateTimePickerField, CheckboxField } from 'components/FormFields';

const FIRST_DAY = 'monday';
const START_HOUR = 0.25;
const END_HOUR = 0.75;

const ERROR_TYPE = {
  NONE: 'NONE',
  OVERLAPPING: 'OVERLAPPING',
  INVALID: 'INVALID',
};

const initialErrors = {
  monday: ERROR_TYPE.NONE,
  tuesday: ERROR_TYPE.NONE,
  wednesday: ERROR_TYPE.NONE,
  thursday: ERROR_TYPE.NONE,
  friday: ERROR_TYPE.NONE,
  saturday: ERROR_TYPE.NONE,
  sunday: ERROR_TYPE.NONE,
};

const baseModel = (index) => ({
  active: false,
  fullDay: false,
  dateRange: [
    {
      from: Number(index + START_HOUR),
      to: Number(index + END_HOUR),
    },
  ],
});

const initialState = {
  monday: { ...baseModel(1) },
  tuesday: { ...baseModel(2) },
  wednesday: { ...baseModel(3) },
  thursday: { ...baseModel(4) },
  friday: { ...baseModel(5) },
  saturday: { ...baseModel(6) },
  sunday: { ...baseModel(7) },
};

const BusinessHoursRulesModal = ({ open, rules, handleClick, onSubmitHours }) => {
  const classes = useStyles();
  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);

  const hasValidRange = (values) => {
    const days = Object.keys(values);
    const newErrors = { ...initialErrors };
    let hasErrors = false;
    days.forEach((day) => {
      // As default we assume that a day is valid (invalid day false)
      let invalidDay = ERROR_TYPE.NONE;
      // Here we clone the array to avoid mutating the original
      const sortedRanges = [...values[day].dateRange].sort((a, b) => a.from - b.from);
      for (
        let idx = 0;
        sortedRanges.length > 0 && idx < sortedRanges.length && !values[day].fullDay; // Skip validation for full days
        idx++
      ) {
        // Here we check if the current range is valid
        // we check if the end time is greater than the start time for this range
        let invalidFromTo = sortedRanges[idx].from >= sortedRanges[idx].to;
        // Here we check if the current range overlaps with the previous range
        // we check if the start time is greater than the previous range end time
        let preciseOverlap = idx > 0 ? sortedRanges[idx - 1].to === sortedRanges[idx].from : false;
        // On overlapping range we set the error type to overlapping and break the loop to avoid further validation
        let overLappingWithPrevious =
          idx > 0 ? sortedRanges[idx - 1].to >= sortedRanges[idx].from : false;
        // On overlapping range we set the error type to overlapping and break the loop to avoid further validation
        if (preciseOverlap) {
          invalidDay = ERROR_TYPE.OVERLAPPING;
          break;
        }
        // On invalid range, we set the error type
        if (invalidFromTo || overLappingWithPrevious) {
          invalidDay = ERROR_TYPE.INVALID;
        }
      }
      newErrors[day] = invalidDay;
      hasErrors = hasErrors || invalidDay !== ERROR_TYPE.NONE;
    });
    setErrors(newErrors);
    return !hasErrors;
  };

  const onSubmit = () => {
    if (hasValidRange(formRef.current.values)) {
      formRef.current.submitForm();
    }
  };

  const applyToAll = (values) => {
    const base = values[FIRST_DAY];

    for (let index = 1; index < days.length; index++) {
      const day = days[index];
      let model = { active: true, fullDay: false };
      if (base.fullDay) {
        model = {
          ...model,
          fullDay: true,
          dateRange: [{ start: index, end: index + 1 }],
        };
      } else {
        model = {
          ...model,
          dateRange: base.dateRange.map((value) => ({
            from: parseFloat(index + 1 + value.from.toString().substring(1)),
            to: parseFloat(index + 1 + value.to.toString().substring(1)),
          })),
        };
      }
      formRef.current.setFieldValue(day, model);
    }
  };

  const handleAddTime = (values, index) => {
    values.push({
      from: Number(index + START_HOUR),
      to: Number(index + END_HOUR),
    });
  };

  const handleRemove = (values, index) => {
    values.remove(index);
  };

  const _rulesDaysValues = days.reduce((acc, cur, i) => {
    acc[cur] = i + 1;
    return acc;
  }, {});

  const handleOnSubmit = (values) => {
    const bhRules = [];
    const keys = Object.keys(values);
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index];
      const row = values[key];

      if (row.fullDay) {
        bhRules.push({
          start: _rulesDaysValues[key],
          end: _rulesDaysValues[key] + 1,
        });
        continue;
      }

      if (!row.active) {
        continue;
      }

      for (let index = 0; index < row.dateRange.length; index++) {
        const child = row.dateRange[index];
        bhRules.push({
          start: child.from,
          end: child.to,
        });
      }
    }

    onSubmitHours(bhRules);
    handleClick();
  };

  const timePickerField = (key, timeValue, indexValue, type) => {
    return (
      <Box>
        <Field
          utc={true}
          time={true}
          hours={true}
          editable="false"
          timeIntervals={30}
          minDate={timeValue}
          dateFormat="h:mm aa"
          timeFormat="h:mm aa"
          showTimeSelect={true}
          showTimeSelectOnly={true}
          style={{ maxWidth: '93%' }}
          day={_rulesDaysValues[key]}
          popperPlacement="bottom-start"
          className={classes.timePicker}
          component={DateTimePickerField}
          label={i18n.t('ruleset.select_hour')}
          id={`${key}.dateRange[${indexValue}].${type}`}
          name={`${key}.dateRange[${indexValue}].${type}`}
        />
      </Box>
    );
  };

  const DaysRows = (key, row, index) => (
    <Grid key={key} container spacing={2} alignItems="center" style={{ width: '100%' }}>
      <Grid item xs={3}>
        <Box>
          <Field
            name={key}
            label={key}
            key={`${key}.active`}
            labelPlacement="end"
            component={CheckboxField}
            onChange={(event) => {
              formRef.current.setFieldValue(`${key}.fullDay`, false);
              // When active day is unchecked we reset the day to the initial state.
              // This will avoid showing the input with invalid day from/to values
              if (!event.target.checked)
                formRef.current.setFieldValue(key, { ...initialState[key] });
            }}
            className={classes.checkField}
            field={{ name: `${key}.active`, value: row[key]?.active }}
          />
        </Box>
      </Grid>
      {!row[key]?.fullDay && (
        <FieldArray
          key={`${key}.dateRange`}
          name={`${key}.dateRange`}
          render={(dateRangeArray) => (
            <>
              {!row[key]?.active && (
                <Grid item xs={2}>
                  <Typography color="secondary">
                    <Box component="span" fontSize={13} fontWeight="fontWeightMedium">
                      {i18n.t('business_hours.closed')}
                    </Box>
                  </Typography>
                </Grid>
              )}

              <Grid item xs={4}>
                {row[key]?.active &&
                  row[key].dateRange.map((element, dateIndex) => (
                    <Box style={{ display: 'flex' }} key={`${key}-dateRange-${dateIndex}`}>
                      <Box style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                        {timePickerField(key, element.dateFrom, dateIndex, 'from')}
                        {dateIndex === row[key].dateRange.length - 1 && (
                          <Box display="flex" justifyContent="center" fontSize={13}>
                            <Button
                              size="small"
                              color="primary"
                              fontSize={13}
                              style={{
                                fontSize: 11,
                                textTransform: 'capitalize',
                              }}
                              data-cy={`${key}-split`}
                              onClick={() => {
                                handleAddTime(dateRangeArray, index + 1);
                              }}
                              startIcon={<AddCircle />}
                            >
                              {i18n.t('business_hours.split_time')}
                            </Button>
                          </Box>
                        )}
                      </Box>

                      <Box style={{ marginLeft: '15px', width: '100%' }}>
                        <>
                          <Box style={{ display: 'flex', position: 'relative' }}>
                            {timePickerField(key, element.dateTo, dateIndex, 'to')}
                            {dateIndex !== 0 && (
                              <Button
                                fontSize={6}
                                onClick={() => {
                                  handleRemove(dateRangeArray, dateIndex);
                                }}
                                style={{
                                  position: 'absolute',
                                  minWidth: '20px',
                                  width: '20px',
                                  padding: 0,
                                  right: 0,
                                }}
                                startIcon={<Clear />}
                              />
                            )}
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="center"
                            marginTop={dateIndex === row[key].dateRange.length - 1 ? '33px' : 0}
                          />
                        </>
                      </Box>
                    </Box>
                  ))}
                {row[key]?.active && errors[key] !== ERROR_TYPE.NONE && (
                  <span className={classes.rangeError}>
                    {i18n.t(`business_hours.errors.${errors[key]}`, {
                      defaultValue: i18n.t('business_hours.errors.DEFAULT'),
                    })}
                  </span>
                )}
              </Grid>
            </>
          )}
        />
      )}

      {row[key]?.fullDay && (
        <Grid item xs={4}>
          <Typography>
            <Box component="span" fontSize={10} fontWeight="fontWeightBold">
              {i18n.t('business_hours.open')}
            </Box>
          </Typography>
        </Grid>
      )}

      {row[key]?.active && (
        <Grid item xs={2}>
          <Box>
            <Field
              key={`${key}.fullDay`}
              name={key}
              labelPlacement="end"
              component={CheckboxField}
              className={classes.checkField}
              label={i18n.t('business_hours.open')}
              field={{ name: `${key}.fullDay`, value: row[key]?.fullDay }}
              onChange={(event) => {
                // When full day is unchecked we reset the day to an active initial state.
                // This will avoid showing the input with invalid day from/to values
                if (!event.target.checked)
                  formRef.current.setFieldValue(key, { ...initialState[key], active: true });
              }}
            />
          </Box>
        </Grid>
      )}

      {row[key]?.active && key === FIRST_DAY && (
        <Grid item xs={2}>
          <Box display="flex" justifyContent="center" fontSize={13}>
            <Button
              color="primary"
              fontSize={13}
              style={{
                fontSize: 11,
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
              }}
              startIcon={<FileCopy />}
              onClick={() => {
                applyToAll(row);
              }}
            >
              {i18n.t('business_hours.apply')}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  useEffect(() => {
    if (rules.length > 0) {
      const values = parseRulesHoursByDays(rules, days, START_HOUR, END_HOUR);
      setInitialValues(values);
    }
  }, [rules]);

  return (
    <BaseModal
      size="lg"
      open={open}
      id="bh-modal"
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      handleClick={handleClick}
      title={i18n.t('business_hours.add_rule')}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={(values) => handleOnSubmit(values)}
        validateOnChange
        validate={
          // Here we validate if theres errors for any day
          Object.keys(errors).reduce((acc, key) => acc || errors[key] !== ERROR_TYPE.NONE, false)
            ? hasValidRange
            : undefined
        }
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid item>
                <Box mb={2}>
                  <Typography data-cy="bhModalTimeTitle">
                    <Box component="span" fontWeight="fontWeightBold" fontSize={14}>
                      {i18n.t('business_hours.select_time')}
                    </Box>
                  </Typography>
                  <Typography data-cy="bhModalTimeDescription">
                    <Box component="span" fontSize={12}>
                      {i18n.t('business_hours.select_time_description')}
                    </Box>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography>
                      <Box component="span" fontSize={14} fontWeight="fontWeightBold">
                        {i18n.t('business_hours.day')}
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={2}>
                    <Typography>
                      <Box component="span" fontSize={14} fontWeight="fontWeightBold">
                        {i18n.t('business_hours.from')}
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={2}>
                    <Typography>
                      <Box component="span" fontSize={14} fontWeight="fontWeightBold">
                        {i18n.t('business_hours.to')}
                      </Box>
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  {days.map((key, index) => {
                    return DaysRows(key, values, index);
                  })}
                </Grid>
              </Form>
            </Grid>
          </Grid>
        )}
      </Formik>
    </BaseModal>
  );
};

BusinessHoursRulesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

BusinessHoursRulesModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default BusinessHoursRulesModal;
